import {
  useGetSingleSensitiveDataQuery,
  useGrantAccessToSensitiveDataMutation,
  useRevokeAccessToSensitiveDataMutation,
} from "apis/sensitiveData";
import classNames from "classnames";
import { Avatar } from "components/DesignSystem/AvatarGroup/Avatar";
import { Button } from "components/DesignSystem/Button/Button";
import { Combobox } from "components/DesignSystem/Combobox/Combobox";
import { SensitiveDataSection } from "components/SensitiveData/SensitiveDataSection";
import Modal from "components/DesignSystem/Modal/Modal";
import { Form, Formik, FormikHelpers, useFormikContext } from "formik";
import { useModal } from "hooks/useModal";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useToast } from "hooks/useToast";
import { useParams } from "react-router-dom";
import { useGetAllCpaTeamsQuery } from "store/apis/serviceTeam";
import { SharedProfile } from "types/Models/sensitiveData";
import { BackendError } from "types/utils/error";

const RevokeAccessModal = ({
  isOpen,
  close,
  name,
  memberId,
}: {
  isOpen: boolean;
  close: () => void;
  name: string;
  memberId: string;
}) => {
  const [revokeAccessToSensitiveData, { isLoading: isDeleting }] =
    useRevokeAccessToSensitiveDataMutation();
  const { entityId } = useParams<{ entityId: string }>();
  const { alertToast, successToast } = useToast();
  const handleRevokeAccess = async () => {
    try {
      await revokeAccessToSensitiveData({
        entityId,
        sensitiveDataMapId: memberId,
      }).unwrap();
      successToast({ message: "Access revoked successfully" });
      close();
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };
  return (
    <Modal.Root open={isOpen} onOpenChange={close}>
      <Modal.Content>
        <Modal.Header>
          <Modal.Title>Revoke Access</Modal.Title>
          <Modal.Close />
        </Modal.Header>
        <Modal.Body>
          <span>
            You are about to revoke access from <strong>{name}</strong>, are you
            sure you want to proceed?
          </span>
        </Modal.Body>
        <Modal.FooterButtonGroup>
          <Button onClick={close}>Cancel</Button>
          <Button
            customType="danger_outlined"
            onClick={handleRevokeAccess}
            isLoading={isDeleting}
          >
            Delete
          </Button>
        </Modal.FooterButtonGroup>
      </Modal.Content>
    </Modal.Root>
  );
};

const SharedProfiles = ({ member }: { member: SharedProfile }) => {
  const {
    isOpen: isAccessModalOpen,
    open: openAccessModal,
    close: closeAccessModal,
  } = useModal();

  return (
    <div
      className={classNames(
        "t-flex t-py-3 t-gap-2 t-items-center t-justify-between t-border t-border-solid  t-border-b-i-surface-grey t-border-t-0 t-border-l-0 t-border-r-0 last:t-border-none"
      )}
    >
      <div className="t-flex t-gap-2 t-items-center">
        <Avatar alt={member.profile.name} />
        <div className=" t-flex t-flex-col">
          <span className="t-text-body-sm">{member.profile.name}</span>
          <span className=" t-text-body-sm">{member.profile.email}</span>
        </div>
      </div>
      <Button onClick={() => openAccessModal()} customType="secondary">
        Revoke
      </Button>

      <RevokeAccessModal
        isOpen={isAccessModalOpen}
        close={closeAccessModal}
        name={member.profile.name}
        memberId={member.uuid}
      />
    </div>
  );
};

const GrantMemberForm = () => {
  const { isCustomer } = useRoleBasedView();
  const { data: membersAccess = [] } = useGetAllCpaTeamsQuery(
    {},
    { skip: isCustomer }
  );
  const {
    values: { invite_profile },
  } = useFormikContext<{ invite_profile: string }>();

  const selectedMember = membersAccess.find(
    ({ uuid }) => uuid === invite_profile
  );
  return (
    <Form>
      <div className="t-flex t-items-end t-gap-2">
        <div className="t-w-full">
          <Combobox
            components={{
              DropdownIndicator: () => null,
            }}
            label="Email"
            required
            withForm
            name="invite_profile"
            menuPlacement="auto"
            menuPortalTarget={document.body}
            options={membersAccess.map((member) => {
              return {
                label: member.name,
                value: member.uuid,
              };
            })}
            value={
              selectedMember
                ? {
                    label: selectedMember.name,
                    value: selectedMember.uuid,
                  }
                : null
            }
          />
        </div>
        <Button customType="primary" size="regular">
          Invite
        </Button>
      </div>
    </Form>
  );
};

export const SensitiveDataExternalDetails = ({
  sensitiveDataId,
}: {
  sensitiveDataId: string;
}) => {
  const { isAdmin } = useRoleBasedView();

  const { alertToast, successToast } = useToast();

  const { entityId } = useParams<{ entityId: string }>();
  const { data } = useGetSingleSensitiveDataQuery(
    {
      entityId,
      formId: sensitiveDataId,
    },
    {
      skip: !entityId || !sensitiveDataId,
    }
  );

  const { shared_profiles } = data || {};

  const [grantAccessToSensitiveData] = useGrantAccessToSensitiveDataMutation();

  const initialValues = {
    invite_profile: "",
  };

  const handleGrantAccess = async (
    value: { invite_profile: string },
    { resetForm }: FormikHelpers<typeof initialValues>
  ) => {
    try {
      const { invite_profile } = value;
      if (invite_profile) {
        await grantAccessToSensitiveData({
          entityId,
          formId: sensitiveDataId,
          profileId: invite_profile,
        }).unwrap();
        resetForm({ values: initialValues });
        successToast({ message: "Access granted successfully" });
      }
    } catch (error) {
      alertToast({ message: (error as BackendError).data?.error?.message });
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <SensitiveDataSection title="External Details">
      <div className="t-flex t-flex-col">
        <Formik initialValues={initialValues} onSubmit={handleGrantAccess}>
          <GrantMemberForm />
        </Formik>
      </div>
      {shared_profiles?.map((member) => (
        <SharedProfiles member={member} key={member.uuid} />
      ))}
    </SensitiveDataSection>
  );
};
